// @flow
import React, { Component, type Node } from 'react';
import { isEmpty, noop } from 'lodash';

import { generateUniqueId } from 'Utils/unique-id/uniqueId';
import { getFormattedLabel } from 'Components/utils/form/form.util';
import { ICONS } from 'Components/utils/icon/icons';

import { Icon, type IconName } from 'Components/frameworks/icon/Icon.jsx';
import { FormControl } from 'Components/frameworks/form-inputs/form-control/FormControl.jsx';

import { themes } from './InputSelect.themes';

type Props = {
  caretIcon?: IconName,
  className?: string,
  hasClear?: boolean,
  iconLeft?: IconName,
  iconRight?: IconName,
  id?: string,
  inputId?: string,
  isDisabled?: boolean,
  isFilled?: boolean,
  isLoading?: boolean,
  isReadOnly?: boolean,
  isRequired?: boolean,
  label?: string | Node,
  name?: string,
  notes?: string,
  onBlur?: (SyntheticEvent<>) => void,
  onChange?: (name: string, value: string) => void,
  onClear?: (SyntheticEvent<>) => void,
  onClick?: (SyntheticEvent<>) => void,
  onFocus?: (SyntheticEvent<>) => void,
  options: SelectProps,
  placeholder?: string,
  size?: SizeProps,
  theme?: string,
  validationState?: string,
  value?: string,
  dataTestId?: string,
  mediumText?: boolean,
};

type State = {
  value: string,
};

export class InputSelect extends Component<Props, State> {
  static defaultProps = {
    caretIcon: ICONS.SELECT,
    options: [],
    size: 'medium',
    theme: 'default',
    mediumText: false,
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      value: props.value || '',
    };
  }

  UNSAFE_componentWillReceiveProps({ value }: Props) {
    this.setState(prevState => {
      if (prevState.value !== value) {
        return { value };
      }
    });
  }

  onChange = (e: SyntheticEvent<>) => {
    const { isLoading, isReadOnly, name = '', onChange = noop } = this.props;

    if (!isReadOnly && !isLoading) {
      e.persist();

      // $FlowFixMe
      const { value } = e.target;

      this.setState(
        {
          value,
        },
        () => onChange(name, value),
      );
    }
  };

  onClear = (e: SyntheticEvent<>) => {
    const { onClear = noop } = this.props;

    this.setState(
      {
        value: '',
      },
      () => onClear(e),
    );
  };

  getOptions(): ?Array<?Node> {
    const { options } = this.props;

    if (isEmpty(options)) {
      return null;
    }

    return options.map(({ isDisabled, isSelected, label, value }) => {
      return label ? (
        <option disabled={isDisabled} key={label} label={label} selected={isSelected} value={value}>
          {label}
        </option>
      ) : null;
    });
  }

  render() {
    const {
      caretIcon,
      className,
      hasClear,
      iconLeft,
      iconRight,
      id = generateUniqueId(
        [typeof this.props.label === 'string' ? this.props.label : ''],
        'InputSelect_',
      ),
      isDisabled,
      isFilled,
      isLoading,
      isReadOnly,
      isRequired,
      label,
      name,
      notes,
      onBlur,
      onClick,
      onFocus,
      placeholder,
      size,
      theme,
      validationState,
      dataTestId,
      inputId,
    } = this.props;

    const { value } = this.state;

    const hasClearEnabled = Boolean(hasClear && this.state.value);
    const hasValue = Boolean(value);

    const placeholderFormatted = getFormattedLabel(placeholder, isRequired);

    const { thCaret, thInput, thInputWrapper, thPlaceholder } = themes[theme](this.props);

    return (
      <FormControl
        className={className}
        hasClear={hasClearEnabled}
        id={id}
        iconLeft={iconLeft}
        iconRight={iconRight}
        isDisabled={isDisabled}
        isFilled={isFilled && hasValue}
        isLoading={isLoading}
        isReadOnly={isReadOnly}
        isRequired={isRequired}
        label={label}
        notes={notes}
        onClear={this.onClear}
        size={size}
        theme={theme}
        validationState={validationState}
      >
        <div className={thInputWrapper}>
          <select
            className={thInput}
            disabled={Boolean(isDisabled || isLoading || isReadOnly)}
            id={inputId ? inputId : id}
            {...(name ? { name } : null)}
            onBlur={onBlur}
            onChange={this.onChange}
            onClick={onClick}
            onFocus={onFocus}
            required={isRequired}
            value={value}
            data-testid={dataTestId}
          >
            {!!placeholderFormatted && (
              <option className={thPlaceholder} label={placeholderFormatted} disabled value="">
                {placeholderFormatted}
              </option>
            )}
            {this.getOptions()}
          </select>
          {!(isDisabled || isLoading || isReadOnly) && (
            <div className={thCaret} data-testid="InputSelectCaret">
              <Icon
                name={caretIcon}
                width="0.625rem"
                data-testid={caretIcon ? `caret-${caretIcon}` : null}
              />
            </div>
          )}
        </div>
      </FormControl>
    );
  }
}
